import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';

import { Filter, useDeleteFilters } from '@ais-ui/websockets';
import { useFilterContext } from '../../../Map/FilterContext';
import { useState } from 'react';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';

interface DeleteFilterProps {
  filter: Filter;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DeleteFilter(props: DeleteFilterProps) {
  const { messages } = useLocalization();
  const filterContext = useFilterContext();
  const { mutate } = useDeleteFilters();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    const filterName = props.filter.name;

    if (!props.filter.uuid) {
      toast({
        className: 'delete-filter-error-toast',
        title: 'Error!',
        description: `${messages.filtersDeleteFilterFailedDescription} ${filterName}`,
        variant: 'destructive',
      });
      props.setShowDialog(false);
      setIsSubmitting(false);
      return;
    }

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'delete',
      'filter',
      setIsSubmitting,
      props.setShowDialog
    );

    const extendedOnSuccess = () => {
      callBacks.onSuccess();

      const uuidExists = filterContext.customFilters.some(
        (existingFilter) => existingFilter.uuid === props.filter.uuid
      );

      if (uuidExists) {
        const newCustomFilters = filterContext.customFilters.filter(
          (existingFilter) => existingFilter.uuid !== props.filter.uuid
        );
        filterContext.setCustomFilters(newCustomFilters);
      }
    };

    const extendedCallbacks = {
      ...callBacks,
      onSuccess: extendedOnSuccess,
    };

    mutate(props.filter.uuid, extendedCallbacks);
  };

  return (
    <Dialog open={props.showDialog} onOpenChange={props.setShowDialog}>
      <DialogTrigger asChild>
        <Button
          id="filter-delete-button"
          variant="ghost"
          className="text-primary hover:text-primary"
        >
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent id="mutate-filter-dialog">
        <DialogHeader>
          <DialogTitle>{messages.filtersDeleteTitle}</DialogTitle>
          <DialogDescription>
            {messages.filtersDeleteFilterWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] 4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.filtersDeleteFilterName}
            </Label>
            <Label htmlFor="filter-name">{props.filter.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              disabled={isSubmitting}
              variant="destructive"
              id="filter-confirm-delete-button"
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
